import { API_CONFIG } from './config';
import Bottleneck from "bottleneck";

const FETCH_TIMEOUT = 60000; // 60 seconds
const PUBLIC_FETCH_TIMEOUT = 60000;

const limiter = new Bottleneck({
  maxConcurrent: 5,
  minTime: 200,
});

async function fetchWithTimeout(resource, options = {}, timeout = FETCH_TIMEOUT) {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeout);
  try {
    const response = await fetch(resource, {
      ...options,
      signal: controller.signal
    });
    clearTimeout(id);
    return response;
  } catch (error) {
    clearTimeout(id);
    throw error;
  }
}

function publicApiHeaders() {
  const key = API_CONFIG.PUBLIC_API_KEY;
  if (!key) {
    throw new Error('REACT_APP_PUBLIC_API_KEY is not configured');
  }
  return { 'x-reckoning-api-key': key };
}

async function fetchPublicWithTimeout(url, options = {}) {
  const response = await fetchWithTimeout(
    url,
    {
      ...options,
      headers: {
        ...publicApiHeaders(),
        ...(options.headers || {}),
      },
    },
    PUBLIC_FETCH_TIMEOUT,
  );
  return response;
}

async function parseJsonResponse(response, fallbackError) {
  const contentType = response.headers.get('content-type') || '';
  const text = await response.text();
  if (!contentType.includes('application/json')) {
    throw new Error(
      fallbackError ||
        'Map API returned HTML instead of JSON. Restart the public UI after adding src/setupProxy.js, and keep REACT_APP_API_BASE_URL unset so /api proxies to the backend on port 3000.',
    );
  }
  try {
    return JSON.parse(text);
  } catch {
    throw new Error(fallbackError || 'Map API returned invalid JSON');
  }
}

export async function fetchPublicGazetteerStates(params = {}) {
  const query = new URLSearchParams(params).toString();
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.PUBLIC.MAP_GAZETTEER_STATES}${query ? `?${query}` : ''}`;
  const response = await fetchPublicWithTimeout(url);
  if (!response.ok) {
    const data = await parseJsonResponse(response, 'Failed to load map states').catch(() => ({}));
    throw new Error(data.message || data.error || `Failed to load map states (${response.status})`);
  }
  return parseJsonResponse(response, 'Failed to load map states');
}

export async function fetchPublicGazetteerMap(params = {}) {
  const query = new URLSearchParams(params).toString();
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.PUBLIC.MAP_GAZETTEER}${query ? `?${query}` : ''}`;
  const response = await fetchPublicWithTimeout(url);
  if (!response.ok) {
    const data = await parseJsonResponse(response, 'Failed to load map agencies').catch(() => ({}));
    throw new Error(data.error || 'Failed to load map agencies');
  }
  return parseJsonResponse(response, 'Failed to load map agencies');
}

export async function fetchToken() {
  const response = await fetchWithTimeout(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.TOKEN}`);
  if (!response.ok) throw new Error('Failed to fetch token');
  return response.json();
}

export async function fetchRecords(params = {}) {
  const query = new URLSearchParams(params).toString();
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.RECORDS}${query ? `?${query}` : ''}`;
  const response = await fetchWithTimeout(url, {
    headers: {
      // Add Authorization header if needed
    }
  });
  if (!response.ok) throw new Error('Failed to fetch records');
  return response.json();
}

export async function fetchAggregate(params = {}) {
  const query = new URLSearchParams(params).toString();
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.AGGREGATE}${query ? `?${query}` : ''}`;
  const response = await fetchWithTimeout(url, {
    headers: {
      // Add Authorization header if needed
    }
  });
  if (!response.ok) throw new Error('Failed to fetch aggregate data');
  return response.json();
}

// Fetch time series data grouped by category using the aggregate endpoint
export async function fetchTimeSeriesByCategory() {
  const query = new URLSearchParams({
    groupBy: "misconduct_event_category,date_of_misconduct_event"
  }).toString();
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.AGGREGATE}?${query}`;
  const response = await fetchWithTimeout(url, {
    headers: {
      // Add Authorization header if needed
    }
  });
  if (!response.ok) throw new Error("Failed to fetch time series by category");
  return response.json();
}

// Fetch aggregate counts by state using the aggregate endpoint
export async function fetchAggregateByState() {
  const query = new URLSearchParams({
    groupBy: "state"
  }).toString();
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.AGGREGATE}?${query}`;
  const response = await fetchWithTimeout(url, {
    headers: {
      // Add Authorization header if needed
    }
  });
  if (!response.ok) throw new Error("Failed to fetch aggregate by state");
  return response.json();
}

export const fetchWithRateLimit = async (url, options) => {
  return limiter.schedule(() => fetch(url, options));
};

/**
 * Submit a misconduct case with file attachments
 * 
 * @param {Object} caseData - The case data
 * @param {File[]} files - Array of files to upload
 * @returns {Promise} - The API response
 */
/**
 * Submit public source materials (files + URLs) for quarantined review.
 */
export async function submitSourceMaterials(formDataBody) {
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.SUBMIT_SOURCE}`;
  const response = await fetchWithTimeout(url, {
    method: 'POST',
    body: formDataBody,
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error || data.message || 'Failed to submit materials');
  }
  return data;
}

export async function submitMisconductCase(caseData, files = []) {
  // Create a FormData object to handle both text data and files
  const formData = new FormData();

  // Add all case data fields to the FormData
  Object.keys(caseData).forEach(key => {
    formData.append(key, caseData[key]);
  });

  // Add each file to the FormData with the same field name "files"
  // This makes it easier for multer to process them as an array
  files.forEach(file => {
    formData.append('files', file);
  });

  // Add metadata about the files
  formData.append('fileCount', files.length);

  try {
    const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.SUBMIT_CASE}`;
    const response = await fetchWithTimeout(url, {
      method: 'POST',
      body: formData,
      // Don't set Content-Type header - the browser will set it with the boundary parameter
      headers: {
        // Add any auth headers if needed
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(errorData?.message || 'Failed to submit case');
    }

    return response.json();
  } catch (error) {
    console.error('Error submitting case:', error);
    throw error;
  }
}

// Example usage
export const getData = async (endpoint) => {
  const response = await fetchWithRateLimit(
    `${process.env.REACT_APP_API_BASE_URL}/${endpoint}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    }
  );
  return response.json();
};